<script setup lang="ts">
const localePath = useLocalePath();
const { locale } = useI18n();

// Default values (optional, set in script or template)
const {
  id = undefined,
  buttonTarget = undefined,
  buttonText = "View more",
  buttonVariant = "white",
  imageLazyLoad = true,
  showImagePlaceholder = true,
} = defineProps<{
  image?: string;
  cta?: unknown;
  id?: string;
  badge?: string;
  title?: string;
  pageType?: string;
  text?: string;
  buttonUrl?: string;
  buttonText?: string;
  buttonVariant?: string;
  buttonTarget?: "_blank" | "_self";
  imageLazyLoad?: boolean;
  showImagePlaceholder?: boolean;
  imageSizes?: string;
}>();

const { data: linkedPageData, execute: fetchLinkedPage } = useFetch(
  `/api/v2/marketing/page-by-id/${id}`,
  {
    key: `page-${id}-${locale.value}`,
    immediate: false,
  },
);

const { data: slugData, execute: fetchSlugData } = useFetch(
  `/api/v2/slugs/page/${id}`,
  {
    key: `slugs-${id}-${locale.value}`,
    immediate: false,
  },
);

if (id) {
  fetchLinkedPage();
}

if (linkedPageData.value && linkedPageData.value?.pageType !== "hotel") {
  fetchSlugData();
}

const pageType = computed(() => {
  return linkedPageData.value?.pageType || "unknown";
});

const localisedSlug = computed(() => {
  const slug =
    slugData.value[locale.value] !== undefined
      ? slugData.value[locale.value]
      : slugData.value["en"];

  return localePath({
    name: pageType.value === "blogArticle" ? "blog-slug" : "slug",
    params: {
      slug: slug.split("/"),
    },
  });
});
</script>

<template>
  <div class="slide contentful-columns-slide">
    <figure v-if="image || showImagePlaceholder" class="cfcs-img img-cover">
      <img
        v-if="typeof image == 'string'"
        :src="image"
        :alt="title"
        :loading="imageLazyLoad ? 'lazy' : 'eager'"
      />

      <ContentfulImage v-else-if="image" v-bind="image" :sizes="imageSizes" />
    </figure>

    <div class="cfcs-body" :class="text ? '' : 'no-text'">
      <Badge
        variant="black"
        v-if="badge"
        class="mb16"
        :class="image ? 'mt24' : ''"
      >
        {{ badge }}
      </Badge>
      <H
        v-if="title"
        level="3"
        size="7"
        class="mb12 cfcs-headline"
        :class="badge ? 'mt0' : image ? 'mt16' : 'mt0'"
        >{{ title }}</H
      >
      <P v-if="text && typeof text === 'string'" class="mt0 mb24">{{ text }}</P>
      <ContentfulAtomRichText
        v-else-if="text"
        :content="text"
        class="mt0 mb24"
      />
    </div>
    <ArrowLink
      class="ctf-arrow-link"
      v-if="!slugData && buttonUrl && buttonText && buttonVariant != 'arrow'"
      :href="buttonUrl"
      :target="buttonTarget"
    >
      {{ buttonText }}
    </ArrowLink>

    <ArrowLink
      class="ctf-arrow-link"
      v-if="slugData"
      :href="localisedSlug"
      :target="buttonTarget"
    >
      {{ buttonText }}
    </ArrowLink>
    <ArrowLink
      class="ctf-arrow-link"
      :href="buttonUrl"
      v-if="buttonUrl && buttonText && buttonVariant === 'arrow'"
      :target="buttonTarget"
    >
      <span>{{ buttonText }}</span>
    </ArrowLink>
  </div>
</template>

<style lang="scss">
.contentful-columns-slide {
  p {
    color: var(--secondary-black);
  }
}

.agile__slide.contentful-columns-slide {
  display: flex;
  flex-direction: column;
  height: auto !important;

  // margin-right: var(--space-24);
  padding: 0 var(--space-12);

  &:last-child {
    margin-right: 0;
  }

  .arrow-link {
    font-family: var(--font-family-bold);

    svg {
      top: 0;
    }
  }

  @include mdx {
    padding: 0 var(--space-6);
  }
}

.cfcs-img {
  @extend %image-cover;

  border-radius: 4px;

  &::after {
    padding-bottom: 75% !important;
  }
}

.cfcs-body {
  flex-grow: 1;

  &.no-text {
    margin-bottom: var(--space-24);
  }
}

.cfcs-btn {
  align-self: flex-start;
}

.cfcs-headline {
  @include mdx {
    font-size: var(--font-size-h7) !important;
    line-height: var(--line-height-h7) !important;
  }
}
</style>
